import React from "react";
import { sanitizeHtml } from "@/lib/sanitize";

// Splits a raw line by `|` into trimmed, non-empty parts.
const splitByPipe = (line) => line.split("|").map((s) => s.trim()).filter(Boolean);

// Decide whether a line should become a bullet list.
// Rule: contains at least one `|` and yields 2+ non-empty parts.
const isBulletLine = (line) => line.includes("|") && splitByPipe(line).length >= 2;

// Rich HTML detection: presence of block tags means we should render as HTML,
// not paragraph-split as plain text.
const looksLikeHtml = (s) => /<\/?(p|div|h[1-6]|ul|ol|li|br|strong|em|b|i|u|a|img|iframe|blockquote|pre|table)\b/i.test(s);

/**
 * Renders content that may be either:
 *   1. Rich HTML (from the RichTextEditor) — sanitized + rendered as-is.
 *   2. Plain text with `|` dividers — each `|`-separated line becomes a bullet list.
 *   3. Plain text — rendered with preserved newlines.
 */
export const PipeText = ({ text, className = "", as: Wrapper = "div", testid }) => {
  if (text == null || text === "") return null;
  const src = String(text);

  // Rich HTML content: run pipe→bullet transform on top of sanitize, then render.
  if (looksLikeHtml(src)) {
    const html = pipeHtmlToBullets(sanitizeHtml(src));
    return (
      <Wrapper
        className={`rich-content ${className}`}
        data-testid={testid}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  // Fast path: plain text with no pipes → keep as-is with pre-line whitespace.
  if (!src.includes("|")) {
    return (
      <Wrapper className={`whitespace-pre-line ${className}`} data-testid={testid}>{src}</Wrapper>
    );
  }

  // Plain text with pipes → mix of paragraphs and bullet lists.
  const lines = src.split(/\r?\n/);
  const nodes = [];
  let buffer = [];

  const flushBuffer = (key) => {
    if (!buffer.length) return;
    nodes.push(
      <p key={`p-${key}`} className="whitespace-pre-line">{buffer.join("\n")}</p>
    );
    buffer = [];
  };

  lines.forEach((line, i) => {
    if (isBulletLine(line)) {
      flushBuffer(i);
      const parts = splitByPipe(line);
      nodes.push(
        <ul key={`ul-${i}`} className="list-disc pl-5 space-y-1 my-1">
          {parts.map((p, j) => <li key={j}>{p}</li>)}
        </ul>
      );
    } else {
      buffer.push(line);
    }
  });
  flushBuffer("tail");

  return <Wrapper className={className} data-testid={testid}>{nodes}</Wrapper>;
};

/**
 * Rewrites the HTML of a contentEditable's blocks so that any block whose
 * plain text uses `|` as a divider gets replaced by a real <ul><li> list.
 * Preserves all other blocks untouched.
 */
export const pipeHtmlToBullets = (html) => {
  if (!html || !html.includes("|")) return html || "";
  if (typeof document === "undefined") return html;
  const container = document.createElement("div");
  container.innerHTML = html;

  const convertTextNode = (node) => {
    const text = node.textContent || "";
    if (!isBulletLine(text)) return null;
    const ul = document.createElement("ul");
    splitByPipe(text).forEach((part) => {
      const li = document.createElement("li");
      li.textContent = part;
      ul.appendChild(li);
    });
    return ul;
  };

  const blocks = Array.from(container.childNodes);
  blocks.forEach((block) => {
    if (block.nodeType === 1 && (block.tagName === "UL" || block.tagName === "OL" || block.tagName === "LI")) return;
    if (block.nodeType === 3) {
      const replacement = convertTextNode(block);
      if (replacement) container.replaceChild(replacement, block);
      return;
    }
    if (block.nodeType !== 1) return;
    const hasNestedBlock = Array.from(block.children).some((c) => /^(P|DIV|UL|OL|H\d|BLOCKQUOTE|PRE|TABLE)$/i.test(c.tagName));
    if (hasNestedBlock) return;
    const replacement = convertTextNode(block);
    if (replacement) container.replaceChild(replacement, block);
  });

  return container.innerHTML;
};
